import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { motion, AnimatePresence } from "framer-motion";
import { X, Info, AlertTriangle, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface AnnouncementTranslation {
  title?: string;
  message?: string;
}

interface Announcement {
  id: string;
  title: string;
  message: string;
  level: string;
  translations: Record<string, AnnouncementTranslation>;
}

const levelStyles: Record<
  string,
  { bg: string; text: string; icon: React.ElementType }
> = {
  info: {
    bg: "bg-primary",
    text: "text-primary-foreground",
    icon: Info,
  },
  warning: {
    bg: "bg-amber-400",
    text: "text-amber-950",
    icon: AlertTriangle,
  },
  critical: {
    bg: "bg-destructive",
    text: "text-destructive-foreground",
    icon: AlertCircle,
  },
};

export function AnnouncementPopup() {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const { i18n } = useTranslation();

  useEffect(() => {
    supabase
      .from("announcements")
      .select("id, title, message, level, translations")
      .eq("active", true)
      .or("expires_at.is.null,expires_at.gt." + new Date().toISOString())
      .order("created_at", { ascending: false })
      .limit(1)
      .then(({ data }) => {
        if (data && data.length > 0) {
          const item = data[0] as unknown as Announcement;
          const key = `announcement_dismissed_${item.id}`;
          if (!sessionStorage.getItem(key)) {
            setAnnouncement(item);
          }
        }
      });
  }, []);

  const dismiss = () => {
    if (announcement) {
      sessionStorage.setItem(`announcement_dismissed_${announcement.id}`, "true");
    }
    setDismissed(true);
  };

  const lang = i18n.language;
  const trans = announcement?.translations?.[lang];
  const displayTitle = trans?.title || announcement?.title || "";
  const displayMessage = trans?.message || announcement?.message || "";

  const style = announcement
    ? levelStyles[announcement.level] || levelStyles.info
    : levelStyles.info;
  const Icon = style.icon;

  return (
    <AnimatePresence>
      {announcement && !dismissed && (
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.95 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed bottom-4 right-4 z-50 max-w-sm"
        >
          <div
            className={cn(
              "rounded-xl shadow-lg p-4 pr-10 relative",
              style.bg,
              style.text
            )}
          >
            <button
              onClick={dismiss}
              className="absolute top-3 right-3 opacity-70 hover:opacity-100 transition-opacity"
              aria-label="Stäng"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex gap-3">
              <Icon className="h-5 w-5 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">{displayTitle}</p>
                <p className="text-sm mt-1 opacity-90">{displayMessage}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
